import React, { useEffect, useRef, useState } from "react";
import { ChevronRight, X } from "lucide-react";
import {
  bulandi2026Meta,
  bulandiRegistrationUpiQrUrl,
} from "../../data/bulandi2026Data";
import {
  BULANDI_REG_DOB_INPUT_MAX,
  BULANDI_REG_DOB_INPUT_MIN,
  clampBulandiRegistrationDobValue,
  readFileAsBase64,
  workshopRegFieldError,
} from "../../utils/bulandiRegistrationFormUtils";
import {
  dismissRegistrationToast,
  toastActionReceived,
  toastRegistrationError,
  toastRegistrationSuccess,
} from "../../utils/registrationToasts";

const inputClass =
  "mt-1.5 w-full rounded-xl border-2 border-bul-border bg-bul-bg px-3 py-2.5 font-mono text-sm text-bul-fg placeholder:text-bul-mutedfg focus:border-bul-lime focus:outline-none focus:ring-2 focus:ring-bul-lime/25";
const labelClass =
  "block font-mono text-xs font-bold uppercase tracking-wider text-bul-mutedfg";

/**
 * @param {{ open: boolean; workshop: { id: string; title: string; feeLabel: string; registrationWebAppUrl: string; whatsappGroupUrl?: string; requiresPaymentScreenshot?: boolean } | null; onClose: () => void; onSuccess: (whatsappGroupUrl: string) => void }} props
 */
export function WorkshopRegistrationDrawer({ open, workshop, onClose, onSuccess }) {
  const submitLockRef = useRef(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [dob, setDob] = useState("");
  const [paymentFile, setPaymentFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  useEffect(() => {
    if (!open) {
      setName("");
      setPhone("");
      setEmail("");
      setDob("");
      setPaymentFile(null);
      setErrors({});
      setSubmitting(false);
      setSubmitError("");
    }
  }, [open, workshop?.id]);

  const needsPaymentScreenshot =
    Boolean(workshop) && workshop.requiresPaymentScreenshot !== false;

  const applyFieldError = (field, value) => {
    if (!workshop) return;
    if (field === "paymentFile" && !needsPaymentScreenshot) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next.paymentFile;
        return next;
      });
      return;
    }
    const msg =
      field === "paymentFile"
        ? workshopRegFieldError.paymentFile(value)
        : workshopRegFieldError[field](value);
    setErrors((prev) => {
      const next = { ...prev };
      if (msg) next[field] = msg;
      else delete next[field];
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");
    if (!workshop) return;
    const paymentRequired = workshop.requiresPaymentScreenshot !== false;

    const next = {};
    for (const [key, val] of [
      ["name", name],
      ["phone", phone],
      ["email", email],
      ["dob", dob],
    ]) {
      const msg = workshopRegFieldError[key](val);
      if (msg) next[key] = msg;
    }
    if (paymentRequired) {
      const payErr = workshopRegFieldError.paymentFile(paymentFile);
      if (payErr) next.paymentFile = payErr;
    }
    setErrors(next);
    if (Object.keys(next).length > 0) {
      const firstErr = Object.values(next).find(Boolean);
      if (firstErr) toastRegistrationError("workshop", firstErr);
      return;
    }

    const webAppUrl = (workshop.registrationWebAppUrl || "").trim();
    if (!webAppUrl) {
      const msg =
        "This workshop’s registration URL is not set yet. Add its /exec URL to src/config/workshopWebApps.json (see scripts/workshops/).";
      setSubmitError(msg);
      toastRegistrationError("workshop", msg);
      return;
    }

    if (submitLockRef.current) return;
    submitLockRef.current = true;
    let tid = null;
    const ws = workshop;
    try {
      setSubmitting(true);
      let paymentFileBase64 = "";
      let safeName = "";
      if (paymentRequired) {
        paymentFileBase64 = await readFileAsBase64(paymentFile);
        safeName = paymentFile.name.replace(/[^\w.\- ]+/g, "_").slice(0, 180);
      }
      const nameTrim = name.trim();
      const emailTrim = email.trim();
      const dobVal = dob;
      const phoneVal = phone;

      tid = toastActionReceived("Workshop registration");
      onClose();

      const payload = {
        action: "workshopRegistration",
        workshopId: ws.id,
        workshopName: ws.title,
        name: nameTrim,
        whatsappNo: phoneVal,
        mail: emailTrim,
        dob: dobVal,
      };
      if (paymentRequired) {
        payload.paymentFileName = safeName || "payment.jpg";
        payload.paymentMimeType = paymentFile.type || "image/jpeg";
        payload.paymentFileBase64 = paymentFileBase64;
      }
      const regSecret = (bulandi2026Meta.registrationSubmitSecret || "").trim();
      if (regSecret) payload.secret = regSecret;

      const res = await fetch(webAppUrl, {
        method: "POST",
        mode: "cors",
        body: JSON.stringify(payload),
        headers: { "Content-Type": "text/plain;charset=utf-8" },
      });
      const text = await res.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        throw new Error(
          "Unexpected server response. Deploy the workshop Apps Script for this sheet and confirm workshopWebApps.json.",
        );
      }
      if (!data.ok) {
        throw new Error(data.error || "Registration failed.");
      }
      if (data.email && data.email.sent) {
        console.info(
          `[Bulandi] Workshop confirmation email sent to ${data.email.recipient} for ${ws.id}.`,
        );
      } else if (data.email) {
        console.warn(
          `[Bulandi] Workshop confirmation email NOT sent for ${ws.id}.`,
          data.email,
        );
      }
      const groupUrlFromServer =
        data.whatsappGroupUrl != null && String(data.whatsappGroupUrl).trim() !== ""
          ? String(data.whatsappGroupUrl).trim()
          : "";
      const whatsappGroupUrl = groupUrlFromServer || (ws.whatsappGroupUrl || "").trim();
      onSuccess(whatsappGroupUrl);
      dismissRegistrationToast(tid);
      toastRegistrationSuccess("workshop", { whatsappGroupUrl });
    } catch (err) {
      dismissRegistrationToast(tid);
      const msg = err?.message || "Something went wrong. Please try again.";
      setSubmitError(msg);
      toastRegistrationError("workshop", msg);
    } finally {
      submitLockRef.current = false;
      setSubmitting(false);
    }
  };

  if (!open || !workshop) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex justify-end bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      role="presentation"
    >
      <aside
        className="flex h-full w-full max-w-md flex-col border-l-2 border-bul-border bg-bul-bg shadow-bul-brutal motion-safe:transition-transform motion-safe:duration-300"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="workshop-reg-drawer-title"
      >
        <div className="relative flex items-center justify-between gap-3 overflow-hidden border-b border-bul-border bg-bul-surface/90 px-5 py-5 sm:px-6">
          <div className="relative min-w-0">
            <p className="font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-bul-lime">
              Bulandi 2026 · Workshop
            </p>
            <h2
              id="workshop-reg-drawer-title"
              className="mt-1 font-display text-[0.9375rem] uppercase tracking-tight text-bul-fg"
            >
              Register
            </h2>
            <p className="mt-1 text-xs font-medium leading-snug text-bul-mutedfg">
              {workshop.title}
            </p>
            <p className="mt-2 rounded-lg border border-bul-cyan/35 bg-bul-cyan/10 px-3 py-2 font-mono text-xs font-bold tabular-nums text-bul-cyan">
              Workshop fee: {workshop.feeLabel}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="relative shrink-0 rounded-full p-2 text-bul-mutedfg transition hover:bg-bul-border/50 hover:text-bul-lime focus:outline-none focus-visible:ring-2 focus-visible:ring-bul-lime focus-visible:ring-offset-2 focus-visible:ring-offset-bul-bg"
            aria-label="Close registration form"
          >
            <X size={22} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-4 sm:px-5 sm:py-5">
            <div>
              <label htmlFor="workshop-reg-name" className={labelClass}>
                Name <span className="text-bul-lime">*</span>
              </label>
              <input
                id="workshop-reg-name"
                type="text"
                autoComplete="name"
                value={name}
                onChange={(e) => {
                  const v = e.target.value;
                  setName(v);
                  applyFieldError("name", v);
                }}
                onBlur={(e) => applyFieldError("name", e.target.value)}
                className={inputClass}
                aria-invalid={errors.name ? "true" : "false"}
              />
              {errors.name ? (
                <p className="mt-1 text-xs text-red-300">{errors.name}</p>
              ) : null}
            </div>

            <div>
              <label htmlFor="workshop-reg-phone" className={labelClass}>
                Phone number <span className="text-bul-lime">*</span>
              </label>
              <input
                id="workshop-reg-phone"
                type="tel"
                inputMode="numeric"
                autoComplete="tel"
                maxLength={10}
                placeholder="10-digit number"
                value={phone}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, "").slice(0, 10);
                  setPhone(v);
                  applyFieldError("phone", v);
                }}
                onBlur={(e) => applyFieldError("phone", e.target.value)}
                className={`${inputClass} tabular-nums`}
                aria-invalid={errors.phone ? "true" : "false"}
              />
              {errors.phone ? (
                <p className="mt-1 text-xs text-red-300">{errors.phone}</p>
              ) : null}
            </div>

            <div>
              <label htmlFor="workshop-reg-email" className={labelClass}>
                Email <span className="text-bul-lime">*</span>
              </label>
              <input
                id="workshop-reg-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => {
                  const v = e.target.value;
                  setEmail(v);
                  applyFieldError("email", v);
                }}
                onBlur={(e) => applyFieldError("email", e.target.value)}
                className={inputClass}
                aria-invalid={errors.email ? "true" : "false"}
              />
              {errors.email ? (
                <p className="mt-1 text-xs text-red-300">{errors.email}</p>
              ) : null}
            </div>

            <div>
              <label htmlFor="workshop-reg-dob" className={labelClass}>
                Date of birth <span className="text-bul-lime">*</span>
              </label>
              <input
                id="workshop-reg-dob"
                type="date"
                min={BULANDI_REG_DOB_INPUT_MIN}
                max={BULANDI_REG_DOB_INPUT_MAX}
                value={dob}
                onChange={(e) => {
                  const v = e.target.value;
                  setDob(v);
                  applyFieldError("dob", v);
                }}
                onBlur={(e) => {
                  const v = clampBulandiRegistrationDobValue(e.target.value.trim());
                  if (v !== e.target.value.trim()) setDob(v);
                  applyFieldError("dob", v);
                }}
                className={inputClass}
                aria-invalid={errors.dob ? "true" : "false"}
              />
              {errors.dob ? (
                <p className="mt-1 text-xs text-red-300">{errors.dob}</p>
              ) : null}
            </div>

            {needsPaymentScreenshot ? (
              <div className="space-y-3 rounded-xl border-2 border-bul-border bg-bul-surface/80 p-4">
                <h3 className="font-display text-xs uppercase tracking-wide text-bul-fg sm:text-[0.75rem]">
                  Payment details
                </h3>
                <p className="text-sm font-bold text-bul-fg">
                  Workshop fee:{" "}
                  <span className="tabular-nums text-bul-lime">
                    {workshop.feeLabel}
                  </span>
                </p>
                <p className="rounded-lg border border-bul-lime/30 bg-bul-lime/10 px-3 py-2 text-xs font-bold text-bul-fg">
                  No cash — pay only via UPI using the QR code below, then upload your
                  payment screenshot.
                </p>
                <p className="text-xs leading-snug text-bul-mutedfg">
                  For any payment issues, please WhatsApp{" "}
                  <a
                    href="https://wa.me/918610447053"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-bold text-bul-lime underline decoration-bul-lime/50 underline-offset-2 hover:text-bul-cyan"
                  >
                    8610447053
                  </a>
                  .
                </p>
                {bulandiRegistrationUpiQrUrl?.trim() ? (
                  <div>
                    <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.25em] text-bul-lime">
                      UPI QR
                    </p>
                    <div className="flex justify-center rounded-lg border-2 border-bul-border bg-transparent p-3">
                      <img
                        src={bulandiRegistrationUpiQrUrl.trim()}
                        alt="SMYM UPI QR code for payment"
                        className="max-h-48 w-auto max-w-full object-contain"
                      />
                    </div>
                  </div>
                ) : null}
                <div>
                  <label htmlFor="workshop-reg-payment" className={labelClass}>
                    Payment screenshot <span className="text-bul-lime">*</span>
                  </label>
                  <input
                    id="workshop-reg-payment"
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const f = e.target.files?.[0] ?? null;
                      setPaymentFile(f);
                      applyFieldError("paymentFile", f);
                    }}
                    onBlur={() => applyFieldError("paymentFile", paymentFile)}
                    className="mt-1.5 block w-full text-sm text-bul-mutedfg file:mr-3 file:rounded-lg file:border-2 file:border-[color:var(--bul-strong-outline)] file:bg-bul-lime file:px-3 file:py-2 file:font-mono file:text-sm file:font-bold file:uppercase file:tracking-wider file:text-[rgb(var(--bul-primary-cta-text-rgb))] hover:file:brightness-110"
                    aria-invalid={errors.paymentFile ? "true" : "false"}
                  />
                  {errors.paymentFile ? (
                    <p className="mt-1 text-xs text-red-300">{errors.paymentFile}</p>
                  ) : null}
                </div>
              </div>
            ) : null}
          </div>

          <div className="shrink-0 space-y-3 border-t border-bul-border bg-bul-surface/95 p-4 backdrop-blur-md sm:p-5">
            {submitError ? (
              <p
                className="rounded-lg border border-red-700/60 bg-red-950/60 px-3 py-2 text-sm text-red-100"
                role="alert"
              >
                {submitError}
              </p>
            ) : null}
            <button
              type="submit"
              disabled={submitting}
              className="bul-cta-primary relative flex w-full items-center justify-center gap-2 py-3.5 text-base"
            >
              {submitting ? (
                "Submitting…"
              ) : (
                <>
                  Submit
                  <ChevronRight
                    className="h-5 w-5 shrink-0"
                    aria-hidden
                    strokeWidth={3}
                  />
                </>
              )}
            </button>
          </div>
        </form>
      </aside>
    </div>
  );
}
